import React from "react";
import { Box, Grid, Card, CardContent, Typography, Paper } from "@mui/material";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
} from "recharts";
import { useTranslation } from "react-i18next";
import { useStore } from "../../store/useStore";

const Analytics: React.FC = () => {
  const { t } = useTranslation();
  const { patients, invoices, employees, suppliers, assets } = useStore();

  // Calculate analytics from store data
  const totalPatients = patients.length;
  const totalInvoices = invoices.length;
  const totalEmployees = employees.length;
  const totalSuppliers = suppliers.length;
  const totalAssets = assets.length;

  // Calculate revenue from invoices
  const totalRevenue = invoices.reduce((sum, invoice) => sum + invoice.amount, 0);
  const averageInvoiceValue = totalInvoices > 0 ? totalRevenue / totalInvoices : 0;

  // Calculate department performance from patients
  const departmentStats = patients.reduce((acc, patient) => {
    const dept = patient.department || 'Unknown';
    if (!acc[dept]) {
      acc[dept] = { patients: 0, revenue: 0 };
    }
    acc[dept].patients += 1;
    // Estimate revenue per patient (this would come from actual billing data)
    acc[dept].revenue += 1000; // Mock revenue per patient
    return acc;
  }, {} as Record<string, { patients: number; revenue: number }>);

  const departmentPerformance = Object.entries(departmentStats).map(([department, stats]) => ({
    department,
    patients: stats.patients,
    revenue: stats.revenue,
  }));

  // Mock data for charts (in a real app, this would come from time-series data)
  const revenueData = [
    { month: "Jan", revenue: 4000, expenses: 2400 },
    { month: "Feb", revenue: 3000, expenses: 1398 },
    { month: "Mar", revenue: 2000, expenses: 9800 },
    { month: "Apr", revenue: 2780, expenses: 3908 },
    { month: "May", revenue: 1890, expenses: 4800 },
    { month: "Jun", revenue: 2390, expenses: 3800 },
  ];

  const patientSatisfaction = [
    { name: "Excellent", value: 45, color: "#4caf50" },
    { name: "Good", value: 35, color: "#8bc34a" },
    { name: "Average", value: 15, color: "#ffc107" },
    { name: "Poor", value: 5, color: "#f44336" },
  ];

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {t("analytics.title")}
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        Business intelligence and advanced analytics
      </Typography>

      {/* Revenue Analysis */}
      <Grid container spacing={3} sx={{ mb: 3, width: "100%" }}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Revenue vs Expenses Trend
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={revenueData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Area
                    type="monotone"
                    dataKey="revenue"
                    stackId="1"
                    stroke="#8884d8"
                    fill="#8884d8"
                  />
                  <Area
                    type="monotone"
                    dataKey="expenses"
                    stackId="1"
                    stroke="#82ca9d"
                    fill="#82ca9d"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Patient Satisfaction
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={patientSatisfaction}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) =>
                      `${name} ${(percent * 100).toFixed(0)}%`
                    }
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {patientSatisfaction.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Department Performance */}
      <Grid container spacing={3} sx={{ width: "100%" }}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Department Performance
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={departmentPerformance}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="department" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="patients" fill="#8884d8" />
                  <Bar dataKey="revenue" fill="#82ca9d" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Key Performance Indicators
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" gutterBottom>
                  Patient Volume: {totalPatients} (+8.2%)
                </Typography>
                <Typography variant="body2" gutterBottom>
                  Total Revenue: SAR {totalRevenue.toLocaleString()}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  Average Invoice: SAR {averageInvoiceValue.toLocaleString()}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  Total Employees: {totalEmployees}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  Total Assets: {totalAssets}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  Active Suppliers: {totalSuppliers}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Analytics;
